import { supabase } from '@ubus/shared';
import type {
  InspectionChecklistItemRow,
  InspectionWorkFields,
  ShiftInspectionGateRecord,
  VehicleInspection,
} from '@ubus/shared';
import { isAfterChecklistComplete, parseFitnessAnswers } from '@ubus/shared';
import dayjs from 'dayjs';

/** Calendar day for shift inspection validity (CONF: natural day; device local for MVP). */
export function inspectionTodayLocal(): string {
  return dayjs().format('YYYY-MM-DD');
}

export async function fetchTodayShiftInspection(
  driverId: string,
  today = inspectionTodayLocal()
): Promise<VehicleInspection | null> {
  const { data, error } = await supabase
    .from('vehicle_inspections')
    .select('*')
    .eq('driver_id', driverId)
    .eq('inspection_type', 'pre-trip')
    .eq('inspection_date', today)
    .not('declaration_accepted_at', 'is', null)
    .in('severity_result', ['pass', 'pass_with_notes'])
    .order('declaration_accepted_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn('[inspectionApi] fetchTodayShiftInspection', error.message);
    return null;
  }
  return data as VehicleInspection | null;
}

export function toGateRecord(insp: VehicleInspection | null): ShiftInspectionGateRecord | null {
  if (!insp) return null;
  return {
    id: insp.id,
    inspection_date: insp.inspection_date,
    declaration_accepted_at: insp.declaration_accepted_at ?? null,
    severity_result: insp.severity_result ?? null,
    confirmed_trip_ids: insp.confirmed_trip_ids ?? [],
    fitness: parseFitnessAnswers(insp.work_fields?.fitness) ?? null,
  };
}

export async function appendConfirmedTrip(
  inspectionId: string,
  tripId: string,
  existing: string[]
): Promise<boolean> {
  if (existing.includes(tripId)) return true;
  const next = [...existing, tripId];
  const { error } = await supabase
    .from('vehicle_inspections')
    .update({ confirmed_trip_ids: next })
    .eq('id', inspectionId);
  if (error) {
    console.warn('[inspectionApi] appendConfirmedTrip', error.message);
    return false;
  }
  return true;
}

export async function updateAfterFields(
  inspectionId: string,
  patch: {
    workFields: InspectionWorkFields;
    afterItems: InspectionChecklistItemRow[];
    afterNote?: string;
  }
): Promise<{ ok: boolean; complete: boolean }> {
  const { data: row } = await supabase
    .from('vehicle_inspections')
    .select('work_fields')
    .eq('id', inspectionId)
    .maybeSingle();

  const work_fields: InspectionWorkFields = {
    ...((row?.work_fields as InspectionWorkFields | null) ?? {}),
    ...patch.workFields,
  };

  const complete = isAfterChecklistComplete(patch.afterItems);

  const updatePayload: Record<string, unknown> = {
    work_fields,
    after_items: patch.afterItems,
    after_completed_at: complete ? new Date().toISOString() : null,
  };
  if (patch.afterNote) {
    updatePayload.notes = `AFTER: ${patch.afterNote}`;
  }

  const { error } = await supabase
    .from('vehicle_inspections')
    .update(updatePayload)
    .eq('id', inspectionId);

  if (error) {
    console.warn('[inspectionApi] updateAfterFields', error.message);
    return { ok: false, complete: false };
  }
  return { ok: true, complete };
}
